import {
  ComponentPropsWithoutRef,
  FC,
  ReactElement,
  ReactNode,
  cloneElement,
} from "react";
import { TRIAL_CTA } from "../const/publicPricing";
import CompanyLogosMarquee from "./CompanyLogosMarquee";
import TrustedBy from "./TrustedBy";
import Typography from "./Typography";
import VideoInput from "./VideoInput";

type PageHeroProps = {
  label?: string;
  // Accept any `ReactNode` (not just a single element) so callers can
  // pass `<>fragments</>` mixing text, `<strong>`, and inline links —
  // see `components/Alternative.tsx` and the opus-clip data file.
  heroTitle: ReactNode;
  heroSubtitle: ReactNode;
  // Optional italic, smaller-text byline rendered directly under the H1
  // (between H1 and subtitle). Used on `/alternatives/opus-clip` to surface
  // an "Last updated … · By …" freshness signal for SEO/E-E-A-T. Omitted
  // (nothing rendered) for every other page.
  freshness?: React.ReactNode;
  caption?: string;
  submitVideoCta?: boolean;
  cta?: ReactElement<HTMLButtonElement>;
  ctaLabel?: string;
  // Forwarded to the embedded <VideoInput /> when no custom `cta` is
  // provided. v2 SEO tool pages use this to customize the URL-input
  // placeholder per page.
  videoInputPlaceholder?: string;
  showCompanyLogos?: boolean;
} & ComponentPropsWithoutRef<"section">;

const PageHero: FC<PageHeroProps> = ({
  label,
  heroTitle,
  heroSubtitle,
  freshness,
  cta,
  submitVideoCta,
  ctaLabel,
  videoInputPlaceholder,
  caption = TRIAL_CTA,
  showCompanyLogos = true,
  ...rest
}) => {
  return (
    <section
      {...rest}
      className={`${rest.className} text-center text-slate-950 flex flex-col items-center max-w-5xl`}
    >
      {/* {label && (
        <div className="mb-8 inline-flex items-center justify-center gap-2 bg-slate-200 rounded-full px-4 py-1 mx-auto">
          <Typography variant="caption" className="text-slate-600 font-medium">
            {label}
          </Typography>
        </div>
      )} */}
      <TrustedBy className="mb-8" />
      <Typography variant="h1" component="h1" className="">
        {heroTitle}
      </Typography>
      {freshness && (
        <Typography
          variant="caption"
          className="italic text-slate-500 mt-3"
        >
          {freshness}
        </Typography>
      )}
      <Typography variant="body1" className="mt-8 text-slate-600">
        {heroSubtitle}
      </Typography>
      {cta ? (
        cloneElement(cta, { className: `${cta?.props.className} mt-12` })
      ) : (
        <VideoInput
          ctaLabel={ctaLabel}
          placeholderText={videoInputPlaceholder}
          className="mt-12 w-full shadow-2xl max-w-xl mx-auto"
        />
      )}
      {caption && (
        <Typography variant="caption" className="text-slate-500 mt-4">
          {caption}
        </Typography>
      )}
      {showCompanyLogos && <CompanyLogosMarquee className="mt-20" />}
    </section>
  );
};

export default PageHero;
